import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Loader2, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import AppShell from '@/components/AppShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUESTIONS_GOODS = [
  { id: 'cr1', category: 'Competitive Rivalry', question: 'How intense is competition in your industry?', options: ['Low', 'Moderate', 'High'] },
  { id: 'cr2', category: 'Competitive Rivalry', question: 'Are there many similar businesses offering the same products?', options: ['Few', 'Some', 'Many'] },
  { id: 'cr3', category: 'Competitive Rivalry', question: 'Is price competition common in your market?', options: ['Rarely', 'Sometimes', 'Frequently'] },
  { id: 'tne1', category: 'Threat of New Entrants', question: 'How easy is it for a new competitor to enter your industry?', options: ['Difficult', 'Moderate', 'Easy'] },
  { id: 'tne2', category: 'Threat of New Entrants', question: 'Are there low startup costs or minimal regulatory barriers?', options: ['No', 'Somewhat', 'Yes'] },
  { id: 'tne3', category: 'Threat of New Entrants', question: 'Does your market attract new players regularly?', options: ['Rarely', 'Sometimes', 'Frequently'] },
  { id: 'bps1', category: 'Bargaining Power of Suppliers', question: 'Are your key supplies or inputs dependent on a few suppliers?', options: ['No', 'Somewhat', 'Yes'] },
  { id: 'bps2', category: 'Bargaining Power of Suppliers', question: 'Are supplier prices volatile or difficult to negotiate?', options: ['No', 'Somewhat', 'Yes'] },
  { id: 'bps3', category: 'Bargaining Power of Suppliers', question: 'Can you easily switch suppliers?', options: ['Easy', 'Moderate', 'Difficult'] },
  { id: 'bpc1', category: 'Bargaining Power of Customers', question: 'Do your customers have strong control over pricing or terms?', options: ['No', 'Somewhat', 'Yes'] },
  { id: 'bpc2', category: 'Bargaining Power of Customers', question: 'Are customers highly price-sensitive?', options: ['No', 'Somewhat', 'Yes'] },
  { id: 'bpc3', category: 'Bargaining Power of Customers', question: 'Are there many alternative products they could easily switch to?', options: ['Few', 'Some', 'Many'] },
  { id: 'ts1', category: 'Threat of Substitutes', question: 'Are there alternative products that could replace yours?', options: ['Few', 'Some', 'Many'] },
  { id: 'ts2', category: 'Threat of Substitutes', question: 'Are those substitute products cheaper or more accessible?', options: ['No', 'Somewhat', 'Yes'] },
  { id: 'ts3', category: 'Threat of Substitutes', question: 'Are consumer preferences changing away from your products?', options: ['No', 'Somewhat', 'Yes'] }
];

const QUESTIONS_SERVICES = [
  { id: 'cr1', category: 'Competitive Rivalry', question: 'How intense is competition in your industry?', options: ['Low', 'Moderate', 'High'] },
  { id: 'cr2', category: 'Competitive Rivalry', question: 'Are there many similar businesses offering the same services?', options: ['Few', 'Some', 'Many'] },
  { id: 'cr3', category: 'Competitive Rivalry', question: 'Is rate/fee competition common in your market?', options: ['Rarely', 'Sometimes', 'Frequently'] },
  { id: 'tne1', category: 'Threat of New Entrants', question: 'How easy is it for a new service provider to enter your industry?', options: ['Difficult', 'Moderate', 'Easy'] },
  { id: 'tne2', category: 'Threat of New Entrants', question: 'Are there low setup costs or minimal regulatory barriers?', options: ['No', 'Somewhat', 'Yes'] },
  { id: 'tne3', category: 'Threat of New Entrants', question: 'Does your market attract new service providers regularly?', options: ['Rarely', 'Sometimes', 'Frequently'] },
  { id: 'bps1', category: 'Bargaining Power of Partners/Resources', question: 'Are your key resources or capabilities dependent on a few partners/vendors?', options: ['No', 'Somewhat', 'Yes'] },
  { id: 'bps2', category: 'Bargaining Power of Partners/Resources', question: 'Are partner/vendor rates volatile or difficult to negotiate?', options: ['No', 'Somewhat', 'Yes'] },
  { id: 'bps3', category: 'Bargaining Power of Partners/Resources', question: 'Can you easily switch partners or vendors?', options: ['Easy', 'Moderate', 'Difficult'] },
  { id: 'bpc1', category: 'Bargaining Power of Customers', question: 'Do your clients have strong control over fees or terms?', options: ['No', 'Somewhat', 'Yes'] },
  { id: 'bpc2', category: 'Bargaining Power of Customers', question: 'Are clients highly rate-sensitive?', options: ['No', 'Somewhat', 'Yes'] },
  { id: 'bpc3', category: 'Bargaining Power of Customers', question: 'Are there many alternative service providers they could easily switch to?', options: ['Few', 'Some', 'Many'] },
  { id: 'ts1', category: 'Threat of Substitutes', question: 'Are there alternative services or solutions that could replace yours?', options: ['Few', 'Some', 'Many'] },
  { id: 'ts2', category: 'Threat of Substitutes', question: 'Are those alternative services cheaper or more accessible?', options: ['No', 'Somewhat', 'Yes'] },
  { id: 'ts3', category: 'Threat of Substitutes', question: 'Are client preferences changing away from your type of service?', options: ['No', 'Somewhat', 'Yes'] }
];

const scoreMap = {
  Low: 1, Moderate: 2, High: 3,
  Few: 1, Some: 2, Many: 3,
  No: 1, Somewhat: 2, Yes: 3,
  Rarely: 1, Sometimes: 2, Frequently: 3,
  Difficult: 1, Easy: 3,
  Medium: 2
};

const QuestionnaireFlow = ({ businessInfo, onAssessmentReady }) => {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const QUESTIONS = businessInfo?.business_type === 'services' ? QUESTIONS_SERVICES : QUESTIONS_GOODS;

  useEffect(() => {
    if (!businessInfo) {
      navigate('/');
    }
  }, [businessInfo, navigate]);

  if (!businessInfo) return null;

  const progress = ((currentQuestion + 1) / QUESTIONS.length) * 100;
  const currentQ = QUESTIONS[currentQuestion];
  const selected = answers[currentQ.id];

  const handleAnswer = (value) => setAnswers({ ...answers, [currentQ.id]: value });

  const handleNext = () => {
    if (currentQuestion < QUESTIONS.length - 1) setCurrentQuestion(currentQuestion + 1);
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) setCurrentQuestion(currentQuestion - 1);
  };

  const handleSubmit = async () => {
    if (!selected) {
      toast.error('Please select an answer');
      return;
    }
    setIsSubmitting(true);
    try {
      const formattedAnswers = QUESTIONS.map((q) => {
        const answer = answers[q.id] || q.options[1];
        return {
          question_id: q.id,
          question: q.question,
          answer,
          score: scoreMap[answer] || 2
        };
      });

      const payload = {
        business_name: businessInfo.business_name,
        industry: businessInfo.industry,
        answers: formattedAnswers
      };

      const response = await axios.post(`${API}/assessment/submit`, payload);
      onAssessmentReady(response.data);
      navigate('/lead-capture');
    } catch (error) {
      console.error('Error submitting assessment:', error);
      toast.error('Failed to submit assessment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppShell>
      {/* Sticky progress */}
      <div className="sticky top-[73px] z-30 bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-500" data-testid="question-counter">
              Step {currentQuestion + 1} of {QUESTIONS.length}
            </div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#004B87]">
              {currentQ.category}
            </div>
          </div>
          <div className="h-[3px] bg-slate-100 relative overflow-hidden">
            <motion.div
              className="absolute inset-y-0 left-0 bg-[#004B87]"
              initial={false}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              data-testid="progress-bar"
            />
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35 }}
          >
            <h2
              className="font-display text-3xl lg:text-[38px] font-light leading-tight text-[#0A192F] mb-10"
              data-testid="current-question"
            >
              {currentQ.question}
            </h2>

            <div className="space-y-3" data-testid="answer-options" role="radiogroup">
              {currentQ.options.map((option) => {
                const isSelected = selected === option;
                return (
                  <button
                    key={option}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => handleAnswer(option)}
                    data-testid={`option-${option.toLowerCase()}`}
                    className={`w-full text-left flex items-center justify-between px-6 py-5 rounded-sm border transition-all duration-200 group ${
                      isSelected
                        ? 'border-[#004B87] bg-[#F0F9FF] ring-1 ring-[#004B87]'
                        : 'border-slate-200 bg-white hover:border-[#0EA5E9] hover:bg-slate-50 hover:-translate-y-[1px]'
                    }`}
                  >
                    <span className={`text-[16px] font-medium ${isSelected ? 'text-[#0A192F]' : 'text-slate-700'}`}>
                      {option}
                    </span>
                    <span
                      className={`flex items-center justify-center w-6 h-6 rounded-full border transition-colors ${
                        isSelected ? 'bg-[#004B87] border-[#004B87]' : 'border-slate-300 group-hover:border-[#0EA5E9]'
                      }`}
                    >
                      {isSelected && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="flex items-center justify-between mt-14 pt-8 border-t border-slate-200">
              <Button
                onClick={handlePrevious}
                disabled={currentQuestion === 0}
                variant="ghost"
                className="text-slate-600 hover:text-[#0A192F] hover:bg-slate-100 rounded-sm text-[13px] uppercase tracking-[0.18em] font-semibold px-5"
                data-testid="previous-button"
              >
                <ChevronLeft className="w-4 h-4 mr-2" strokeWidth={2} />
                Previous
              </Button>

              {currentQuestion < QUESTIONS.length - 1 ? (
                <Button
                  onClick={handleNext}
                  disabled={!selected}
                  className="bg-[#004B87] hover:bg-[#0A192F] text-white rounded-sm text-[13px] uppercase tracking-[0.18em] font-semibold px-6 h-11 shadow-none disabled:opacity-40 transition-colors"
                  data-testid="next-button"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-2" strokeWidth={2} />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={!selected || isSubmitting}
                  className="bg-[#004B87] hover:bg-[#0A192F] text-white rounded-sm text-[13px] uppercase tracking-[0.18em] font-semibold px-6 h-11 shadow-none disabled:opacity-40 transition-colors"
                  data-testid="submit-button"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analysing...
                    </>
                  ) : (
                    <>
                      Complete Assessment
                      <ChevronRight className="w-4 h-4 ml-2" strokeWidth={2} />
                    </>
                  )}
                </Button>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </AppShell>
  );
};

export default QuestionnaireFlow;
